package com.ijoin.data.repository

import com.ijoin.data.api.TicketApi
import com.ijoin.data.model.CreateFreeTicketsRequest
import com.ijoin.data.model.Ticket
import com.ijoin.data.storage.TicketCache

data class TicketFetchResult(
    val tickets: List<Ticket>,
    val fromCache: Boolean,
    val error: Throwable? = null
)

class TicketRepository(
    private val ticketApi: TicketApi = TicketApi(),
    private val ticketCache: TicketCache = TicketCache()
) {
    suspend fun getUserTickets(): Result<List<Ticket>> {
        val result = ticketApi.getUserTickets()
        result.onSuccess { tickets ->
            ticketCache.saveTickets(tickets)
        }

        if (result.isFailure) {
            val cached = ticketCache.getTickets()
            if (cached.isNotEmpty()) {
                return Result.success(cached)
            }
        }

        return result
    }

    suspend fun fetchUserTickets(): TicketFetchResult {
        val cached = ticketCache.getTickets()
        val result = ticketApi.getUserTickets()

        return if (result.isSuccess) {
            val tickets = result.getOrThrow()
            ticketCache.saveTickets(tickets)
            TicketFetchResult(tickets = tickets, fromCache = false)
        } else {
            val error = result.exceptionOrNull()
            if (cached.isNotEmpty()) {
                TicketFetchResult(tickets = cached, fromCache = true, error = error)
            } else {
                TicketFetchResult(tickets = emptyList(), fromCache = false, error = error)
            }
        }
    }

    fun getCachedTickets(): List<Ticket> = ticketCache.getTickets()

    fun clearCachedTickets() {
        ticketCache.clear()
    }

    suspend fun getUpcomingTickets(): Result<List<Ticket>> {
        return ticketApi.getUpcomingTickets()
    }

    suspend fun getFavoriteTickets(): Result<List<Ticket>> {
        return ticketApi.getFavoriteTickets()
    }

    suspend fun getTicketById(ticketId: String): Result<Ticket> {
        return ticketApi.getTicketById(ticketId)
    }

    suspend fun toggleFavorite(ticketId: String): Result<Ticket> {
        return ticketApi.toggleFavorite(ticketId)
    }

    suspend fun cancelTicket(ticketId: String): Result<Ticket> {
        val result = ticketApi.cancelTicket(ticketId)
        result.onSuccess { cancelled ->
            val cached = ticketCache.getTickets()
            if (cached.isNotEmpty()) {
                val updated = cached.map { if (it.id == ticketId) cancelled else it }
                ticketCache.saveTickets(updated)
            }
        }
        return result
    }

    suspend fun issueFreeTickets(request: CreateFreeTicketsRequest): Result<List<Ticket>> {
        return ticketApi.createFreeTickets(request)
    }

    suspend fun getTicketsByBooking(bookingId: Long): Result<List<Ticket>> {
        return ticketApi.getTicketsByBooking(bookingId)
    }

    suspend fun getTicketsByPayment(paymentId: String): Result<List<Ticket>> {
        return ticketApi.getTicketsByPayment(paymentId)
    }
}
